const { useCallback, useContext, useLayoutEffect, useState, useSyncExternalStore } = require('react');
const { ReactReduxContext } = require('react-redux');
const { useInView } = require('react-intersection-observer');
const Configuration = require('../app-context/configuration');
const { profileImageLoader } = require('../services/profile-image-loader');

const internals = {};

module.exports = { useProfileImage, useProfileImageVisibility };

function useProfileImageVisibility(eager = false) {

    // The viewport also clips nested scrolling containers, and works for persistent chrome.
    return useInView({ rootMargin: '200px', skip: eager, initialInView: eager, fallbackInView: true });
}

function useProfileImage(source, { enabled = true, priority = 0 } = {}) {

    const src = typeof source === 'string' ? source : '';
    const managed = internals.isManagedSource(src);

    // Keep the shared photo components usable outside the app's Redux provider too.
    const store = useContext(ReactReduxContext)?.store;
    const previewEnabled = useSyncExternalStore(
        store?.subscribe || internals.subscribeToNothing,
        useCallback(() => store?.getState()?.app?.debug?.imageEmptyStates === true, [store]),
        internals.previewDisabled
    );
    const preview = managed && previewEnabled;
    const [result, setResult] = useState(null);
    const [nativeError, setNativeError] = useState(null);
    const subscribed = !preview && (enabled || (result?.source === src && result.status === 'loaded'));

    // Read and subscribe before paint so a retained blob does not flash a loading state.
    useLayoutEffect(() => {

        if (!managed || !subscribed || !src) {
            if (preview) {
                setResult(null);
            }

            return;
        }

        return profileImageLoader.subscribe(src, (snapshot) => {

            setResult({ ...snapshot, source: src });
        }, { priority });
    }, [src, managed, subscribed, priority, preview]);

    const onError = () => {

        if (managed) {
            profileImageLoader.invalidate(src);
        }
        else {
            setNativeError(src);
        }
    };

    if (!src || preview) {
        return { status: 'missing', src: null, onError };
    }

    if (!subscribed) {
        return { status: 'loading', src: null, onError };
    }

    if (!managed) {
        return { status: nativeError === src ? 'error' : 'loaded', src: nativeError === src ? null : src, onError };
    }

    return { ...(result?.source === src ? result : { status: 'loading', src: null }), onError };
}

internals.isManagedSource = (src) => {

    if (!src || /^(?:data:|blob:)/i.test(src)) {
        return false;
    }

    try {
        const base = new URL(Configuration.nearpeerApi.baseUrl());
        const url = new URL(src, base);
        return url.origin === base.origin && url.pathname.startsWith(`${base.pathname.replace(/\/$/, '')}/image/`) &&
            /\/image\/(?:50|100|200|400|600|800|1000)\//.test(url.pathname);
    }
    catch (error) {
        return false;
    }
};

internals.noop = () => {};

internals.subscribeToNothing = () => internals.noop;

internals.previewDisabled = () => false;
